/**
 * Wraps an array of entry items into the `*Collection` shape expected
 * by the Traverser and downstream CMS consumers.
 *
 * @param key - The content type identifier (e.g. 'header', 'page', 'categoryPage').
 * @param items - Array of entry objects to wrap.
 * @param total
 * @returns An object like `{ headerCollection: { __typename: 'HeaderCollection', items: [...] } }`.
 */
export function wrapItemsAsCollection(key: string, items: any[], total?: number): Record<string, any> {
	const collectionKey = key.endsWith('Collection') ? key : `${key}Collection`;

	return {
		[collectionKey]: {
			__typename: collectionKey.charAt(0).toUpperCase() + collectionKey.slice(1),
			items,
			...(total !== undefined && {total}),
		},
	};
}

/**
 * Recursively searches a CMS content tree for the first node matching the predicate.
 * Walks through all `*Collection.items` arrays at each level.
 *
 * @param node - The root node to search from (e.g. a page item).
 * @param predicate - A function that returns true for the desired node.
 * @returns The first matching node, or null if not found.
 */
export function findInContentTree(node: any, predicate: (item: any) => boolean): any | null {
	if (!node || typeof node !== 'object') {
		return null;
	}

	if (predicate(node)) {
		return node;
	}

	for (const key of Object.keys(node)) {
		if (key.endsWith('Collection') && node[key]?.items) {
			for (const item of node[key].items) {
				const found = findInContentTree(item, predicate);

				if (found) {
					return found;
				}
			}
		}
	}

	return null;
}

/**
 * Extracts `items` from a raw ContentHub API response envelope and wraps them
 * into the `*Collection` shape.
 *
 * Returns `null` when the response is missing, empty, or not an Array-type response.
 *
 * @param contentType - The content type identifier (e.g. 'header', 'page').
 * @param response - Raw ContentHub API response (`{ sys: { type: 'Array' }, items: [...] }`).
 */
export function normalizeContenthubResponse(
	contentType: string,
	response: {sys?: {type: string}; items?: any[]} | null,
): Record<string, any> | null {
	if (!response?.items || response?.sys?.type !== 'Array') {
		return null;
	}

	return wrapItemsAsCollection(contentType, response.items);
}
